import React, { useEffect, useReducer, useState } from "react";
import { Action, GameState, initState, reducer } from "./gameLogic";

type GameProps = {
  game: GameState;
  dispatch: React.Dispatch<Action>;
};

const TOUCH_THRESHOLD = 40;

const points = (pts: number) => (
  <span>
    {pts} <span className="y">*</span>
  </span>
);

const GameMessage = ({ game, dispatch }: GameProps) => (
  <div className="snek__message">
    {game.state == "Crashed" && (
      <p>You crashed! You got {points(game.score)}.</p>
    )}
    {game.state == "Paused" && <p>Game paused</p>}
    <a
      href="#"
      onClick={(e) => {
        e.preventDefault();
        dispatch("Start");
      }}
    >
      [
      {
        {
          Crashed: "Restart game",
          Paused: "Continue game",
          Waiting: "Start game",
        }[game.state]
      }
      ]
    </a>
  </div>
);

export type SnekProps = {
  /** Coordinates for the snek (array of `[x,y]` values, e.g. `[[2,1], [1,1]]`) */
  snek: GameState["snek"];
  /** Where is he pointing */
  direction: GameState["direction"];
};
export const Snek = ({ snek, direction }: SnekProps) => (
  <>
    {snek.map(([x, y], i) => (
      <div
        key={i}
        className={`snek__part ${
          i == 0 ? `snek__part--head snek__part--head-${direction}` : ""
        }`}
        data-testid={`snek__part--${x}-${y}-${direction}`}
        style={{ gridColumn: x + 1, gridRow: y + 1 }}
      />
    ))}
  </>
);

type AppleProps = { apple: GameState["apple"] };
const Apple = ({ apple }: AppleProps) => (
  <div
    className="snek__apple"
    data-testid={`snek__apple--${apple[0]}-${apple[1]}`}
    style={{
      gridColumn: apple[0] + 1,
      gridRow: apple[1] + 1,
    }}
  />
);

const GameBoard = ({ game, dispatch }: GameProps) => (
  <div className="snek-wrap">
    <div
      className="snek"
      style={{
        gridTemplate: `repeat(${game.width}, 1fr) / repeat(${game.height}, 1fr)`,
      }}
    >
      {game.state == "Running" ? (
        <>
          <Snek snek={game.snek} direction={game.direction} />
          <Apple apple={game.apple} />
        </>
      ) : (
        <GameMessage game={game} dispatch={dispatch} />
      )}
    </div>
    <div className="snek__score">
      {points(game.score)}
      <span className="snek__meta">
        {" "}
        – <a href="http://snek.aurlien.net">original</a> –{" "}
        <a href="https://adventofcode.com/2022/day/9">inspiration</a>
      </span>
    </div>
  </div>
);

type SnekGameProps = { initialGameState?: GameState };
const SnekGame = ({ initialGameState = initState() }: SnekGameProps) => {
  const [game, dispatch] = useReducer(reducer, initialGameState);

  useEffect(() => {
    const delay = game.speedUp ? Math.min(100, game.delay) : game.delay;
    const timer = setTimeout(() => dispatch("Move"), delay);
    return () => clearTimeout(timer);
  }, [game.tick, game.state]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case "ArrowUp":
        case "ArrowDown":
        case "ArrowLeft":
        case "ArrowRight":
          dispatch(e.key.slice(5) as Action);
          break;
        case "Enter":
          dispatch("Start");
          break;
        case " ":
          dispatch("Pause");
          break;
      }
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      switch (e.key) {
        case "ArrowUp":
        case "ArrowDown":
        case "ArrowLeft":
        case "ArrowRight":
          dispatch((e.key.slice(5) + "Up") as Action);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("keyup", handleKeyUp);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("keyup", handleKeyUp);
    };
  }, []);

  const [isTouching, setIsTouching] = useState(false);
  const [moved, setMoved] = useState(false);
  const [startTouch, setStartTouch] = useState<[number, number]>();

  const touchStart = (e: React.TouchEvent) => {
    if (!isTouching) {
      setIsTouching(true);
      setMoved(false);
      setStartTouch([e.targetTouches[0].clientX, e.targetTouches[0].clientY]);
    }
  };

  const touchMove = (e: React.TouchEvent) => {
    if (isTouching && !moved) {
      const delta = [
        e.targetTouches[0].clientX - startTouch[0],
        e.targetTouches[0].clientY - startTouch[1],
      ];
      const absDelta = Math.sqrt(Math.pow(delta[0], 2) + Math.pow(delta[1], 2));
      if (absDelta > TOUCH_THRESHOLD) {
        dispatch(
          Math.abs(delta[0]) > Math.abs(delta[1])
            ? delta[0] > 0
              ? "Right"
              : "Left"
            : delta[1] > 0
            ? "Down"
            : "Up"
        );
        setMoved(true);
      }
    }
  };

  const touchEnd = (e: React.TouchEvent) => {
    setIsTouching(false);
  };

  return (
    <div
      onTouchStart={touchStart}
      onTouchMove={touchMove}
      onTouchEnd={touchEnd}
      onTouchCancel={touchEnd}
    >
      <GameBoard game={game} dispatch={dispatch} />
    </div>
  );
};

export default SnekGame;
